/**
 * PURPOSE: Compañía efectiva por pestaña para la administración por compañía (FTR-0012).
 * RESPONSIBILITIES: Cargar las compañías administrables del actor (GET /api/hayllis/v2/admin/companies,
 *   el mismo servicio que aplica la frontera en el backend), resolver ?idcompany= contra ellas,
 *   exponer el selector y aplicar/limpiar el contexto de cabecera X-Hayllis-Company antes de que
 *   los hijos hagan requests. Nada se guarda en la sesión del servidor; localStorage solo preselecciona.
 * COLLABORATORS: src/hooks/effectiveCompany.utils.js, src/services/effectiveCompanyContext.js,
 *   src/services/adminCompanyService.js, react-router-dom, Redux auth.
 * ROUTE: winscore/src/hooks/useEffectiveCompany.js
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { getAdministrableCompanies } from "../services/adminCompanyService.js";
import {
  clearActiveCompanyContext,
  parseCompanyId,
  setActiveCompanyContext,
} from "../services/effectiveCompanyContext.js";
import {
  LAST_COMPANY_STORAGE_KEY,
  resolveEffectiveCompanySelection,
} from "./effectiveCompany.utils.js";

const readRememberedCompanyId = () => {
  try {
    return window.localStorage.getItem(LAST_COMPANY_STORAGE_KEY);
  } catch {
    return null;
  }
};

const rememberCompanyId = (companyId) => {
  try {
    window.localStorage.setItem(LAST_COMPANY_STORAGE_KEY, String(companyId));
  } catch {
    // Almacenamiento no disponible: la preselección es solo una comodidad.
  }
};

export const useEffectiveCompany = () => {
  const userId = useSelector((state) => state.auth.user?.iduser);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawUrlCompanyId = searchParams.get("idcompany");
  const [load, setLoad] = useState({
    status: "loading",
    companies: [],
    unrestricted: false,
    error: null,
  });

  useEffect(() => {
    if (!userId) return undefined;

    let active = true;
    setLoad((current) => ({ ...current, status: "loading", error: null }));
    getAdministrableCompanies()
      .then(({ companies, unrestricted }) => {
        if (active) setLoad({ status: "loaded", companies, unrestricted, error: null });
      })
      .catch((error) => {
        if (!active) return;
        setLoad({
          status: error?.response?.status === 403 ? "forbidden" : "error",
          companies: [],
          unrestricted: false,
          error,
        });
      });

    return () => {
      active = false;
    };
  }, [userId]);

  const selection = useMemo(() => {
    if (load.status !== "loaded") {
      return { status: load.status, effectiveCompanyId: null, redirectCompanyId: null };
    }
    return resolveEffectiveCompanySelection({
      urlCompanyId: rawUrlCompanyId,
      allowedCompanyIds: load.companies.map((company) => company.id),
      rememberedCompanyId: readRememberedCompanyId(),
    });
  }, [load, rawUrlCompanyId]);

  const setCompany = useCallback(
    (companyId) => {
      const id = parseCompanyId(String(companyId ?? ""));
      if (!id) return;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("idcompany", String(id));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (selection.status === "redirect" && selection.redirectCompanyId) {
      setCompany(selection.redirectCompanyId);
    }
  }, [selection.status, selection.redirectCompanyId, setCompany]);

  useEffect(() => {
    if (selection.effectiveCompanyId) rememberCompanyId(selection.effectiveCompanyId);
  }, [selection.effectiveCompanyId]);

  // Layout effect: el contexto queda aplicado antes de los efectos pasivos (fetch) de los hijos,
  // y se limpia al cambiar de compañía o al desmontar la vista administrativa.
  useLayoutEffect(() => {
    if (!selection.effectiveCompanyId) return undefined;
    setActiveCompanyContext(selection.effectiveCompanyId);
    return () => clearActiveCompanyContext(selection.effectiveCompanyId);
  }, [selection.effectiveCompanyId]);

  const company = useMemo(
    () => load.companies.find((item) => item.id === selection.effectiveCompanyId) || null,
    [load.companies, selection.effectiveCompanyId]
  );

  return {
    status: selection.status,
    effectiveCompanyId: selection.effectiveCompanyId,
    company,
    companies: load.companies,
    canSwitch: load.companies.length > 1,
    unrestricted: load.unrestricted,
    setCompany,
    error: load.error,
  };
};

export default useEffectiveCompany;
